import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomLayout from "../../Layout/CustomLayout";
import {
  Typography,
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Image,
  Table,
  Input,
  Space,
  Select,
  Tag,
  message,
} from "antd";
import { SearchOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import NoDataPage from "../../NoDataPages/NoDataPage";
import investigationImage from "../../../assets/images/nurseIcons/investigation.svg";
import inputStyles from "../../../styles/Custom/Input.module.css";
import calendarImg from "../../../assets/images/nurseIcons/calender.svg";
import { getIPReportAPI } from "../../../services/orgDashboard.services";
import { getFacilityListApi } from "../../../services/facilities.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axiosInstance from "../../../config/axios.config";
import FinalDischargeReportTable from "../FinalDischargeDashboard/FinalDischargeReportTable";
import FinalDischargeFiltersRow from "../FinalDischargeDashboard/FinalDischargeFiltersRow";
import { useFinalDischargeReport } from "../FinalDischargeDashboard/useFinalDischargeReport";
import {
  parsePageParam,
  parseSearchParam,
  updateUrlSearchParams,
} from "../../../helpers/urlFilters";
import useQuery from "../../../hooks/useQuery";
import "../FinalDischargeDashboard/FinalDischargeDashboard.css";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";
const disabledDate = (current) => {
  return current && current > dayjs().endOf("day");
};
dayjs.extend(isoWeek);

const FINAL_DISCHARGE_REPORT = "final_discharge";

const IPDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useQuery();
  const isFinalDischargeRoute = location.pathname.includes("final-discharge-dashboard");
  const reportType =
    params?.report === FINAL_DISCHARGE_REPORT || isFinalDischargeRoute
      ? FINAL_DISCHARGE_REPORT
      : params?.report || "active_admissions";

  const facilityAdmin =
    getCookie(Role) === "Facility_Admin" ? getCookie(FacilityID) : null;
  const endOfMonth = dayjs().endOf("month");
  const searchText = parseSearchParam(params) || "";
  const page = parsePageParam(params);
  const currentFacility = facilityAdmin
    ? Number(facilityAdmin)
    : params?.facility
      ? Number(params.facility)
      : null;
  const date = {
    start_date: params?.start_date || "",
    end_date: params?.end_date || "",
  };

  const [searchInput, setSearchInput] = useState(searchText);
  const [facilityData, setFacilityData] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: page,
    pageSize: 100,
    total: 0,
  });
  const debounceRef = useRef(null);
  const [singleFacilityName, setSingleFacilityName] = useState(null);

  const isFinalDischarge = reportType === FINAL_DISCHARGE_REPORT;
  const finalDischargeReport = useFinalDischargeReport({ enabled: isFinalDischarge });

  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: page }));
  }, [page]);

  useEffect(() => {
  getFacilityListApi()
    .then((res) => {
      const data = res?.data || [];

      const facilityOptions = data.map((option) => ({
        label: option.name,
        value: option.id,
      }));

      if (facilityOptions.length === 1) {
        setSingleFacilityName(facilityOptions[0].label);
        setFacilityData(facilityOptions);
      } else {
        setFacilityData([{ label: "All", value: null }, ...facilityOptions]);
      }
    })
    .catch((error) => console.log(`Error:${error}`));
}, []);


  const reportTypeOptions = [
    { label: "Active Admissions", value: "active_admissions" },
    { label: "New Admissions", value: "new_admissions" },
    { label: "Final Discharge Dashboard", value: FINAL_DISCHARGE_REPORT },
  ];

  const onDateChange = (range) => {
    if (!range?.[0] || !range?.[1]) return;
    updateUrlSearchParams(navigate, {
      start_date: dayjs(range[0]).format(dateFormat),
      end_date: dayjs(range[1]).format(dateFormat),
      page: 1,
    });
  };

  const fetchIPReport = async (pageNum = page, pageSize = 100) => {
    setLoading(true);
    // const params = {
    //   facility: currentFacility,
    //   start_date: date.start_date,
    //   end_date: date.end_date,
    //   page,
    //   page_size: pageSize,
    //   search: searchText || undefined,
    // };

    // Remove undefined parameters
    // Object.keys(params).forEach(
    //   (key) => params[key] === undefined && delete params[key]
    // );

    try {
      const res = await getIPReportAPI(
        currentFacility,
        date,
        pageNum,
        pageSize,
        searchText,
        reportType,
      );
      setData(res?.data?.data || []);
      setPagination({
        current: res?.data?.current_page || 1,
        pageSize: pageSize,
        total: res?.data?.total_count || 0,
      });
    } catch (error) {
      console.error(`Error:${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (nextPagination) => {
    updateUrlSearchParams(navigate, { page: nextPagination.current });
    fetchIPReport(nextPagination.current, nextPagination.pageSize);
  };

useEffect(() => {
  if (!isFinalDischarge && date?.start_date && date?.end_date && reportType) {
    fetchIPReport(page, pagination.pageSize);
  }
   // eslint-disable-next-line
}, [currentFacility, date.start_date, date.end_date, reportType, page, searchText]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateUrlSearchParams(navigate, { search: value || null, page: 1 });
    }, 1000);
  };

  // Generate dynamic columns based on data structure
  const generateColumns = () => {
    if (!data || data.length === 0) return [];

    const sampleRecord = data[0];
    const allKeys = Object.keys(sampleRecord);

    // Define custom renderers for specific fields
    const customRenderers = {
      age_gender: (_, record) =>
        `${record.age ?? "-"}, ${record.gender ?? "-"}`,
      admission_date: (date) =>
        date ? dayjs(date).format("MMM DD, YYYY") : "-",
      discharge_date: (date) =>
        date && date !== "None" ? dayjs(date).format("MMM DD, YYYY") : "None",
    };

    return allKeys.map((key) => {
      const column = {
        title: key
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        dataIndex: key,
        key: key,
        width: 150,
        fixed: key === "UID" ? "left" : undefined,
      };

      // Apply custom renderer if exists
      if (customRenderers[key]) {
        column.render = customRenderers[key];
      }

      return column;
    });
  };

  const columns = generateColumns();

  const handleExport = async (type) => {
    if (!date.start_date || !date.end_date) {
    message.error("Please select the date filters to download the file");
    return;
  }

    try {
      const params = {
        facility: currentFacility,
        start_date: date.start_date,
        end_date: date.end_date,
        response_type: type, // 'csv' or 'xlsx'
        search: searchText || undefined,
        report_type: reportType,

      };
      Object.keys(params).forEach((k) => params[k] == null && delete params[k]);
      const query = new URLSearchParams(params).toString();
      const url = `/billing/dashboard/ip-report/?${query}`;
      // Use axiosInstance to get blob
      axiosInstance({
        url,
        method: "GET",
        responseType: "blob",
        headers: {
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "Content-Disposition": 'attachment; filename="ip_report.xlsx"',
        },
      })
        .then((response) => {
          const blob = response.data;
          let filename = `ip_report.${type}`;
          // Try to get filename from headers
          const contentDisposition = response.headers["content-disposition"];
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match) filename = match[1];
          }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Export failed", error);
        });
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  return (
    <CustomLayout
      header={
        <Typography className={typoStyles.h6}>
          {isFinalDischarge ? "Final Discharge Dashboard" : "Inpatients"}
        </Typography>
      }
      sideContent={<SideMenu />}
    >
      <Card
        style={{ margin: 16 }}
        title={
          <Space>
            <Typography.Text style={{ fontSize: 20, fontWeight: 600 }}>
              {isFinalDischarge ? "Final Discharge Dashboard" : "Inpatients"}
            </Typography.Text>
            {!isFinalDischarge && date?.start_date && date?.end_date && (
              <Tag color="purple">
                {" "}
                {dayjs(date.start_date).format("MMM DD, YYYY")} to{" "}
                {dayjs(date.end_date).format("MMM DD, YYYY")}
              </Tag>
            )}
            {isFinalDischarge &&
              finalDischargeReport.date?.from_date &&
              finalDischargeReport.date?.to_date && (
                <Tag color="purple">
                  {dayjs(finalDischargeReport.date.from_date).format("MMM DD, YYYY")}{" "}
                  to{" "}
                  {dayjs(finalDischargeReport.date.to_date).format("MMM DD, YYYY")}
                </Tag>
              )}
          </Space>
        }
      >
        <Row gutter={8} align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Report Type</div>
            <Select
              value={reportType}
              options={reportTypeOptions}
              onChange={(val) => {
                updateUrlSearchParams(navigate, {
                  report: val,
                  page: 1,
                  search: null,
                  start_date: val === FINAL_DISCHARGE_REPORT ? null : date.start_date || null,
                  end_date: val === FINAL_DISCHARGE_REPORT ? null : date.end_date || null,
                  from_date: val === FINAL_DISCHARGE_REPORT ? undefined : null,
                  to_date: val === FINAL_DISCHARGE_REPORT ? undefined : null,
                });
              }}
              style={{ width: 260, marginRight: 8 }}
              size="large"
            />
          </Col>
          {isFinalDischarge ? (
            <FinalDischargeFiltersRow {...finalDischargeReport} />
          ) : (
            <>
              {!facilityAdmin && (
                <Col>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Facility</div>
                  {singleFacilityName ? (
                    <Typography.Text
                      style={{
                        display: "inline-block",
                        padding: "6px 12px",
                        borderRadius: 6,
                        background: "#f5f5f5",
                        fontWeight: 500,
                      }}
                    >
                      {singleFacilityName}
                    </Typography.Text>
                  ) : (
                    <Select
                      value={currentFacility}
                      options={facilityData}
                      onChange={(val) =>
                        updateUrlSearchParams(navigate, { facility: val, page: 1 })
                      }
                      style={{ width: 150 }}
                      size="large"
                      loading={loading}
                    />
                  )}
                </Col>
              )}

              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>
                  Start Date & End Date
                </div>
                <RangePicker
                  style={{
                    color: "black",
                    border: "1px solid #a182e3",
                    height: "38px",
                  }}
                  disabledDate={(current) =>
                    current && current.isAfter(endOfMonth, "day")
                  }
                  className={inputStyles.mdDateInput}
                  onChange={onDateChange}
                  allowClear={false}
                  value={[
                    date?.start_date ? dayjs(date.start_date) : null,
                    date?.end_date ? dayjs(date.end_date) : null,
                  ]}
                  suffixIcon={
                    <Image width={20} src={calendarImg} preview={false} />
                  }
                  loading={loading}
                />
              </Col>

              <Col flex="auto" />

              <Col>
                <Input
                  placeholder="Search Inpatients"
                  prefix={<SearchOutlined />}
                  style={{
                    width: 220,
                    marginTop: 24,
                    borderRadius: 8,
                  }}
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  allowClear
                  loading={loading}
                />
              </Col>

              <Col>
                <Space style={{ marginTop: 24 }}>
                  <Button
                    icon={<CloudDownloadOutlined />}
                    style={{
                      background:
                        "color-mix(in srgb, var(--ant-primary-color) 10%, white)",
                      color: "var(--ant-primary-color)",
                      border: 0,
                      borderRadius: 8,
                      fontWeight: 500,
                    }}
                    onClick={() => handleExport("xlsx")}
                    loading={loading}
                  >
                    Excel
                  </Button>
                  <Button
                    icon={<CloudDownloadOutlined />}
                    type="primary"
                    onClick={() => handleExport("csv")}
                    loading={loading}
                  >
                    CSV
                  </Button>
                </Space>
              </Col>
            </>
          )}
        </Row>

        {isFinalDischarge ? (
          <FinalDischargeReportTable
            data={finalDischargeReport.data}
            columnConfig={finalDischargeReport.columnConfig}
            loading={finalDischargeReport.loading}
            pagination={finalDischargeReport.pagination}
            onTableChange={finalDischargeReport.handleTableChange}
          />
        ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="admission_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            position: ["bottomCenter"],
          }}
          scroll={{ x: "max-content", y: "calc(100vh - 320px)" }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <NoDataPage
                title="No Data found"
                subtitle="Select a date range to view data"
                image={investigationImage}
              />
            ),
          }}
        />
        )}
      </Card>
    </CustomLayout>
  );
};

export default IPDashboard;
